import type { PnlHierarchyMember } from 'src/components/star-grid/starGridTypes';
import { MEMBER_PATH_DELIMITER } from 'src/components/star-grid/utils/subset-editor/memberSubset';

export interface HierarchyFilterRow {
  /** Stable across siblings that repeat an id: the tree is addressed by PATH, not by id alone. */
  readonly path: string;
  readonly id: string;
  readonly label: string;
  readonly depth: number;
  readonly hasChildren: boolean;
  readonly expanded: boolean;
}

// Joins with MEMBER_PATH_DELIMITER, not "/": member ids themselves contain "/", so a "/" join would
// collide two distinct tree positions and leak their expand/search state onto each other.
export const hierarchyRowPath = (parentPath: string, id: string): string => (parentPath ? `${parentPath}${MEMBER_PATH_DELIMITER}${id}` : id);

const matches = (member: PnlHierarchyMember, query: string): boolean => member.label.toLowerCase().includes(query);

/**
 * Paths of every member matching the query, plus every ancestor of a match.
 *
 * Ancestors are included so a match stays reachable: showing "2531 - Appstore - Fire TV" with its
 * FireTV / Total FireTV / EDS parents omitted would present a leaf as if it were a root.
 */
export const hierarchySearchPaths = (members: readonly PnlHierarchyMember[], query: string): ReadonlySet<string> => {
  const trimmed = query.trim().toLowerCase();
  const keep = new Set<string>();
  if (!trimmed) return keep;
  const walk = (list: readonly PnlHierarchyMember[], parentPath: string): boolean => {
    let anyKept = false;
    list.forEach((member) => {
      const path = hierarchyRowPath(parentPath, member.id);
      const childKept = walk(member.children, path);
      if (childKept || matches(member, trimmed)) {
        keep.add(path);
        anyKept = true;
      }
    });
    return anyKept;
  };
  walk(members, '');
  return keep;
};

/**
 * The visible rows, depth-first, honouring expansion.
 *
 * While searching, every surviving branch is treated as expanded — a match the user cannot see
 * without first expanding its parents is not a search result.
 */
export const hierarchyFilterRows = (
  members: readonly PnlHierarchyMember[],
  expanded: ReadonlySet<string>,
  searchPaths?: ReadonlySet<string>
): HierarchyFilterRow[] => {
  const rows: HierarchyFilterRow[] = [];
  const walk = (list: readonly PnlHierarchyMember[], parentPath: string, depth: number) => {
    list.forEach((member) => {
      const path = hierarchyRowPath(parentPath, member.id);
      if (searchPaths && !searchPaths.has(path)) return;
      const hasChildren = member.children.length > 0;
      const isExpanded = searchPaths ? hasChildren : expanded.has(path);
      rows.push({ path, id: member.id, label: member.label, depth, hasChildren, expanded: isExpanded });
      if (hasChildren && isExpanded) walk(member.children, path, depth + 1);
    });
  };
  walk(members, '', 0);
  return rows;
};

/** Paths of the top level only — what "collapse all" leaves behind. */
export const hierarchyRootPaths = (members: readonly PnlHierarchyMember[]): string[] => members.map((member) => member.id);

/**
 * The members a filter may offer: the `scopeRoot` subtree, or everything when it declares none.
 *
 * The root is kept as a row rather than replaced by its children, because selecting the roll-up
 * itself ("Echo/Alexa/CHA/Dash/Frames") is a legitimate — and usually the default — choice.
 */
export const hierarchyScopedMembers = (members: readonly PnlHierarchyMember[], scopeRoot?: string | null): readonly PnlHierarchyMember[] => {
  if (!scopeRoot) return members;
  const find = (list: readonly PnlHierarchyMember[]): PnlHierarchyMember | undefined => {
    for (const member of list) {
      if (member.id === scopeRoot) return member;
      const hit = find(member.children);
      if (hit) return hit;
    }
    return undefined;
  };
  const root = find(members);
  // Not found: show the whole dimension rather than an empty picker -- a scope naming a member the
  // tree does not contain is a config problem, and an empty filter hides it.
  return root ? [root] : members;
};
